import { FC, useCallback, useRef } from 'react';
import { InlineLoading } from '@carbon/react';
import { ColumnDef } from '@tanstack/react-table';
import { useAtom } from 'jotai';
import { DataTable } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { PlainLink } from 'components/link';
import { formatNumber } from 'components/search/common/format-helpers';
import { useLoadableAtomValue } from 'lib/hooks/useLoadableAtomValue';
import {
  wellSpacingDetailsAtom,
  triggerPageAtomFamily,
  triggerPageSizeAtomFamily,
  offsetPageAtomFamily,
  offsetPageSizeAtomFamily,
} from './atoms';
import { SpacingStatusCell } from './status-cells';
import { formatDuration } from './utils';
import type { SpacingDetail, WellSpacingsData } from './types';
import styles from './page.module.scss';

interface WellSpacingsSubTableProps {
  requestId: number;
}

// Shared columns
const uwiColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'subjectUwi14',
  id: 'subjectUwi14',
  header: 'UWI',
  size: 170,
  cell: (info) => {
    const href = info.row.original.wellHref;
    const value = info.getValue() as string;
    return href ? (
      <PlainLink href={href} className={styles.wellLink}>
        {value}
      </PlainLink>
    ) : (
      <span className={styles.wellLink}>{value}</span>
    );
  },
};

const wellNameColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'wellName',
  id: 'wellName',
  header: 'Well Name',
  size: 200,
  meta: { type: 'text' },
  cell: (info) => (info.getValue() as string | null) || '\u2014',
};

const statusColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'status',
  id: 'status',
  header: 'Status',
  size: 130,
  cell: (info) => <SpacingStatusCell status={info.getValue() as string} />,
};

const evalPointsColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'evalPointsDone',
  id: 'evalPoints',
  header: 'Eval Pts (Done/Total)',
  size: 160,
  cell: (info) => {
    const done = info.getValue() as number;
    const total = info.row.original.evalPointsTotal;
    if (total === 0) return '\u2014';
    return (
      <span className={styles.mono}>
        {formatNumber(done)}/{formatNumber(total)}
      </span>
    );
  },
};

// Trigger-specific: shows Search Area
const searchAreaColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'searchAreaFt',
  id: 'searchAreaFt',
  header: 'Search Area',
  size: 120,
  cell: (info) => `${formatNumber(info.getValue() as number)} ft`,
};

// Offset-specific: shows Duration
const durationColumn: ColumnDef<SpacingDetail, any> = {
  accessorKey: 'durationSeconds',
  id: 'durationSeconds',
  header: 'Duration',
  size: 100,
  cell: (info) => formatDuration(info.getValue() as number | null),
};

const triggerColumns = [
  uwiColumn,
  wellNameColumn,
  statusColumn,
  evalPointsColumn,
  searchAreaColumn,
];

const offsetColumns = [
  uwiColumn,
  wellNameColumn,
  statusColumn,
  evalPointsColumn,
  durationColumn,
];

export const WellSpacingsSubTable: FC<WellSpacingsSubTableProps> = ({
  requestId,
}) => {
  const result = useLoadableAtomValue<WellSpacingsData, Error>(
    wellSpacingDetailsAtom(requestId)
  );
  const [triggerPage, setTriggerPage] = useAtom(
    triggerPageAtomFamily(requestId)
  );
  const [triggerPageSize, setTriggerPageSize] = useAtom(
    triggerPageSizeAtomFamily(requestId)
  );
  const [offsetPage, setOffsetPage] = useAtom(offsetPageAtomFamily(requestId));
  const [offsetPageSize, setOffsetPageSize] = useAtom(
    offsetPageSizeAtomFamily(requestId)
  );

  const handleTriggerLoadData = useCallback(
    ({ page: newPage, pageSize: newPageSize }: TableState) => {
      setTriggerPage(newPage);
      setTriggerPageSize(newPageSize);
    },
    [setTriggerPage, setTriggerPageSize]
  );

  const handleOffsetLoadData = useCallback(
    ({ page: newPage, pageSize: newPageSize }: TableState) => {
      setOffsetPage(newPage);
      setOffsetPageSize(newPageSize);
    },
    [setOffsetPage, setOffsetPageSize]
  );

  // Cache the last successful payload so polling-driven refetches don't flicker
  // the sub-table back to the loading spinner. The ref persists across renders;
  // we only fall back to loading/error states when there's no cached data yet.
  const lastDataRef = useRef<WellSpacingsData | null>(null);
  if (result.state === 'hasData') {
    lastDataRef.current = result.data;
  }

  if (result.state === 'loading' && lastDataRef.current === null) {
    return (
      <div className={styles.expandedRowContent}>
        <InlineLoading description="Loading well spacings..." />
      </div>
    );
  }

  if (result.state === 'hasError' && lastDataRef.current === null) {
    return (
      <div className={styles.expandedRowContent}>
        <div className={styles.expandedRowError}>{result.error.message}</div>
      </div>
    );
  }

  const wsData =
    result.state === 'hasData'
      ? result.data
      : (lastDataRef.current as WellSpacingsData);

  if (wsData.totalCount === 0 && wsData.offsetWellCount === 0) {
    return (
      <div className={styles.expandedRowContent}>
        <div className={styles.expandedRowEmpty}>
          No well spacings found for this request.
        </div>
      </div>
    );
  }

  // DataTable manages pagination state internally and has no controlled-page
  // prop — remount each table via `key` when its own page/pageSize atom
  // changes, so the table's internal state stays in sync with the atom.
  return (
    <div className={styles.expandedRowContent}>
      {wsData.totalCount > 0 && (
        <>
          <div className={styles.sectionLabel}>
            Trigger Wellbore &mdash; as subject vs.{' '}
            {formatNumber(wsData.offsetWellCount)} offset well
            {wsData.offsetWellCount !== 1 ? 's' : ''}
          </div>
          <DataTable
            key={`trigger-${triggerPage}-${triggerPageSize}`}
            data={wsData.triggerSpacings}
            columns={triggerColumns}
            getRowId={(row) => String(row.id)}
            persistenceKey={`ws-trigger-spacings-${requestId}`}
            initialTableState={{
              page: triggerPage,
              pageSize: triggerPageSize,
            }}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: wsData.totalCount,
            }}
            onPaginationChange={handleTriggerLoadData}
            onSortChange={handleTriggerLoadData}
            onFilterChange={handleTriggerLoadData}
            onGlobalSearchChange={handleTriggerLoadData}
            config={{
              columnResizing: false,
              columnReordering: false,
              columnPinning: false,
              sorting: false,
              columnVisibility: false,
              globalSearch: false,
              columnFilters: false,
              export: false,
            }}
          />
        </>
      )}

      {wsData.offsetWellCount > 0 && (
        <>
          <div className={styles.sectionLabel}>
            Offset Wellbores &mdash; each as subject vs. trigger well only
            <span className={styles.offsetMeta}>
              {' '}
              ({formatNumber(wsData.offsetWellCount)} wells within search area)
            </span>
          </div>
          <DataTable
            key={`offset-${offsetPage}-${offsetPageSize}`}
            data={wsData.offsetSpacings}
            columns={offsetColumns}
            getRowId={(row) => String(row.id)}
            persistenceKey={`ws-offset-spacings-${requestId}`}
            initialTableState={{
              page: offsetPage,
              pageSize: offsetPageSize,
            }}
            scroll={{
              mode: 'paginated',
              serverDriven: true,
              totalCount: wsData.offsetWellCount,
            }}
            onPaginationChange={handleOffsetLoadData}
            onSortChange={handleOffsetLoadData}
            onFilterChange={handleOffsetLoadData}
            onGlobalSearchChange={handleOffsetLoadData}
            config={{
              columnResizing: false,
              columnReordering: false,
              columnPinning: false,
              sorting: false,
              columnVisibility: false,
              globalSearch: false,
              columnFilters: false,
              export: false,
            }}
          />
        </>
      )}
    </div>
  );
};
